import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BriefcaseBusiness, Clock3, MapPin, Phone, Search, Trash2, UserRound, UsersRound } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import API from '../../utils/api';
import { onAdminRefresh } from '../../utils/adminSocket';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import './IncompleteRegistrations.css';

const ageOf = (value) => {
    if (!value) return '-';
    const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return `${Math.floor(seconds / 86400)} day${seconds < 172800 ? '' : 's'} ago`;
};

const IncompleteRegistrations = () => {
    const [drafts, setDrafts] = useState([]);
    const [counts, setCounts] = useState({ member: 0, service: 0 });
    const [role, setRole] = useState('all');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);

    const loadDrafts = useCallback(async () => {
        try {
            const response = await API.get('/admin/registration-drafts');
            setDrafts(response.data.drafts || []);
            setCounts(response.data.counts || { member: 0, service: 0 });
        } catch (error) {
            if (error.response?.status !== 429) toast.error(error.response?.data?.message || 'Could not load incomplete registrations');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDrafts();
        const offRefresh = onAdminRefresh(loadDrafts);
        const interval = setInterval(loadDrafts, 30000);
        return () => { offRefresh(); clearInterval(interval); };
    }, [loadDrafts]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return drafts.filter(draft => {
            if (role !== 'all' && draft.role !== role) return false;
            if (!needle) return true;
            const detail = draft.details || {};
            return [draft.mobile, detail.name, detail.profileName, detail.businessName, detail.ownerName, detail.email, detail.city]
                .filter(Boolean).some(value => String(value).toLowerCase().includes(needle));
        });
    }, [drafts, query, role]);

    const [confirm, confirmDialog] = useConfirmDialog();

    const removeDraft = async (draft) => {
        const name = draft.details?.businessName || draft.details?.profileName || draft.details?.name || draft.mobile;
        if (!(await confirm({
            title: `Remove the incomplete registration for ${name}?`,
            message: 'This person had not finished registering. Their saved details are deleted and they can start again any time.',
            items: ['The saved registration details', 'Nothing else is affected, because no account was created yet'],
            confirmLabel: 'Remove', danger: true, icon: Trash2,
        }))) return;
        try {
            await API.delete(`/admin/registration-drafts/${draft._id}`);
            setDrafts(current => current.filter(item => item._id !== draft._id));
            setCounts(current => ({ ...current, [draft.role]: Math.max(0, (current[draft.role] || 0) - 1) }));
            toast.success('Incomplete registration removed');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Could not remove registration');
        }
    };

    const total = counts.member + counts.service;

    return (
        <div className="incomplete-layout">
            {confirmDialog}
            <Sidebar />
            <main className="admin-content incomplete-content">
                <Navbar title={<span className="incomplete-title"><Clock3 size={19} /> Incomplete Profiles</span>} />
                <div className="incomplete-page">
                    <header className="incomplete-heading">
                        <div>
                            <h2>Incomplete registrations</h2>
                            <p>People who verified their mobile number but have not completed registration yet.</p>
                        </div>
                        <span className="incomplete-total"><UsersRound size={16} /> {total} pending</span>
                    </header>

                    <section className="incomplete-panel">
                        <div className="incomplete-toolbar">
                            <label className="incomplete-search"><Search size={17} /><input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search name, mobile, email or city..." /></label>
                            <div className="incomplete-tabs">
                                {[['all', 'All', total], ['member', 'Members', counts.member], ['service', 'Service providers', counts.service]].map(([value, label, count]) => (
                                    <button type="button" key={value} className={role === value ? 'active' : ''} onClick={() => setRole(value)}>{label}<span>{count}</span></button>
                                ))}
                            </div>
                        </div>

                        {loading ? <div className="incomplete-state">Loading incomplete registrations...</div> : visible.length === 0 ? (
                            <div className="incomplete-state"><Clock3 size={36} /><strong>No incomplete registrations</strong><span>OTP-verified drafts will appear here automatically.</span></div>
                        ) : (
                            <div className="incomplete-grid">
                                {visible.map(draft => {
                                    const detail = draft.details || {};
                                    const isService = draft.role === 'service';
                                    const name = isService ? (detail.businessName || detail.ownerName) : (detail.profileName || detail.name);
                                    const location = [detail.city, detail.district, detail.state].filter(Boolean).join(', ');
                                    return (
                                        <article className="incomplete-card" key={draft._id}>
                                            <div className="incomplete-card-head">
                                                <span className={`incomplete-role-icon ${isService ? 'service' : 'member'}`}>{isService ? <BriefcaseBusiness size={20} /> : <UserRound size={20} />}</span>
                                                <div><h3>{name || 'Name not entered'}</h3><span>{isService ? 'Service provider' : 'Matrimony member'} · Step {draft.lastStep || 1}</span></div>
                                                <span className="incomplete-badge">Incomplete</span>
                                            </div>
                                            <div className="incomplete-details">
                                                <span><Phone size={15} /> +91 {draft.mobile}</span>
                                                {detail.email && <span>{detail.email}</span>}
                                                {location && <span><MapPin size={15} /> {location}</span>}
                                                {isService && detail.category && <span>Category: {detail.category}</span>}
                                                {!isService && detail.religion && <span>{[detail.religion, detail.caste, detail.subCaste].filter(Boolean).join(' · ')}</span>}
                                            </div>
                                            <footer><span title={new Date(draft.updatedAt).toLocaleString()}><Clock3 size={14} /> Last activity {ageOf(draft.updatedAt)}</span><button type="button" onClick={() => removeDraft(draft)} title="Remove incomplete registration"><Trash2 size={16} /> Remove</button></footer>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </div>
            </main>
        </div>
    );
};

export default IncompleteRegistrations;
